/**
 * ConfigPanel Component
 *
 * Pannello di configurazione per il bot WhatsApp AI.
 * Gestisce: API keys, provider TTS, voci, prompt AI.
 *
 * @example
 * ```tsx
 * import { WhatsAppProvider } from '../WhatsAppProvider';
 * import { ConfigPanel } from './ConfigPanel';
 *
 * function Settings() {
 *   return (
 *     <WhatsAppProvider config={{ apiBaseUrl: '/api' }}>
 *       <ConfigPanel />
 *     </WhatsAppProvider>
 *   );
 * }
 * ```
 */

import { useState, useEffect } from 'react';
import { useWhatsAppConfig } from '../WhatsAppProvider';
import { waAuthHeaders } from '../waFetch';

// Prompt Editor Modal Component
interface PromptEditorModalProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  description: string;
  value: string;
  onChange: (value: string) => void;
  defaultValue: string;
}

// Confirmation Modal Component
interface ConfirmModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  title: string;
  message: string;
  confirmText?: string;
  cancelText?: string;
  confirmColor?: 'red' | 'yellow' | 'green';
}

function ConfirmModal({ isOpen, onClose, onConfirm, title, message, confirmText = 'Conferma', cancelText = 'Annulla', confirmColor = 'yellow' }: ConfirmModalProps) {
  if (!isOpen) return null;

  const colorClasses = {
    red: 'bg-red-600 hover:bg-red-700',
    yellow: 'bg-yellow-600 hover:bg-yellow-700',
    green: 'bg-green-600 hover:bg-green-700'
  };

  return (
    <div className="fixed inset-0 z-[99999] flex items-center justify-center bg-black/70 backdrop-blur-sm">
      <div className="bg-slate-800 rounded-lg border border-slate-600 shadow-2xl w-[90vw] max-w-md">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-slate-700">
          <h3 className="text-lg font-semibold text-white flex items-center gap-2">
            <span className="text-yellow-400">⚠️</span> {title}
          </h3>
          <button
            onClick={onClose}
            className="p-2 hover:bg-slate-700 rounded transition text-slate-400 hover:text-white"
          >
            ✕
          </button>
        </div>

        {/* Content */}
        <div className="p-4">
          <p className="text-slate-300 text-sm">{message}</p>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-2 p-4 border-t border-slate-700">
          <button
            onClick={onClose}
            className="px-4 py-2 bg-slate-600 hover:bg-slate-500 text-white rounded text-sm font-medium transition"
          >
            {cancelText}
          </button>
          <button
            onClick={() => { onConfirm(); onClose(); }}
            className={`px-4 py-2 ${colorClasses[confirmColor]} text-white rounded text-sm font-medium transition`}
          >
            {confirmText}
          </button>
        </div>
      </div>
    </div>
  );
}

function PromptEditorModal({ isOpen, onClose, title, description, value, onChange, defaultValue }: PromptEditorModalProps) {
  const [localValue, setLocalValue] = useState(value);

  useEffect(() => {
    setLocalValue(value);
  }, [value, isOpen]);

  if (!isOpen) return null;

  const handleSave = () => {
    onChange(localValue);
    onClose();
  };

  const handleReset = () => {
    setLocalValue(defaultValue);
  };

  return (
    <div className="fixed inset-0 z-[99999] flex items-center justify-center bg-black/70 backdrop-blur-sm">
      <div className="bg-slate-800 rounded-lg border border-slate-600 shadow-2xl w-[90vw] max-w-4xl max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-slate-700">
          <div>
            <h3 className="text-lg font-semibold text-white">{title}</h3>
            <p className="text-xs text-slate-400 mt-1">{description}</p>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-slate-700 rounded transition text-slate-400 hover:text-white"
          >
            ✕
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 p-4 overflow-auto">
          <textarea
            value={localValue}
            onChange={(e) => setLocalValue(e.target.value)}
            className="w-full h-[50vh] min-h-[300px] px-3 py-2 bg-slate-900 border border-slate-600 rounded text-white text-sm focus:outline-none focus:border-primary-500 font-mono resize-none"
            placeholder="Inserisci il prompt..."
          />
          <div className="mt-2 text-xs text-slate-500">
            {localValue.length} caratteri
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between p-4 border-t border-slate-700">
          <button
            onClick={handleReset}
            className="px-4 py-2 bg-yellow-600 hover:bg-yellow-700 text-white rounded text-sm font-medium transition flex items-center gap-2"
          >
            🔄 Ripristina Default
          </button>
          <div className="flex gap-2">
            <button
              onClick={onClose}
              className="px-4 py-2 bg-slate-600 hover:bg-slate-500 text-white rounded text-sm font-medium transition"
            >
              Annulla
            </button>
            <button
              onClick={handleSave}
              className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded text-sm font-medium transition"
            >
              Salva Prompt
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

interface Config {
  id: number;
  phone_number: string;
  is_active: boolean;
  auto_response: boolean;
  audio_response_enabled: boolean;
  reply_only_known_numbers: boolean;
  openai_model: string;
  tts_voice: string;
  max_tokens: number;
  temperature: number;
  openai_api_key?: string;
  elevenlabs_api_key?: string;
  google_tts_api_key?: string;
  azure_tts_api_key?: string;
  tts_provider?: string;
  tts_voice_elevenlabs?: string;
  tts_voice_google?: string;
  tts_voice_azure?: string;
  prompt_system?: string;
  prompt_intent_recognition?: string;
  prompt_response_generation?: string;
  prompt_device_query?: string;
}

interface ElevenLabsCredits {
  tier?: string;
  character_count?: number;
  character_limit?: number;
  next_character_count_reset_unix?: number;
}

/** Preset voci per provider TTS */
const VOICE_PRESETS = {
  openai: [
    { id: 'alloy', name: 'Alloy - Neutrale' },
    { id: 'echo', name: 'Echo - Maschile' },
    { id: 'fable', name: 'Fable - Maschile UK' },
    { id: 'onyx', name: 'Onyx - Maschile profondo' },
    { id: 'nova', name: 'Nova - Femminile energica' },
    { id: 'shimmer', name: 'Shimmer - Femminile dolce' },
  ],
  elevenlabs: [
    { id: 'CiwzbDpaN3pQXjTgx3ML', name: 'Francesca - Italiana naturale' },
    { id: '21m00Tcm4TlvDq8ikWAM', name: 'Rachel - Voce calma' },
    { id: 'AZnzlk1XvdvUeBnXmlld', name: 'Domi - Voce forte' },
    { id: 'EXAVITQu4vr4xnSDxMaL', name: 'Bella - Voce espressiva' },
    { id: 'ErXwobaYiN019PkySvjV', name: 'Antoni - Voce maschile' },
    { id: 'MF3mGyEYCl7XYWbV9V6O', name: 'Elli - Voce femminile energica' },
  ],
  google: [
    { id: 'it-IT-Standard-A', name: 'Standard A - Femminile' },
    { id: 'it-IT-Standard-B', name: 'Standard B - Femminile 2' },
    { id: 'it-IT-Standard-C', name: 'Standard C - Maschile' },
    { id: 'it-IT-Standard-D', name: 'Standard D - Maschile 2' },
    { id: 'it-IT-Wavenet-A', name: 'Wavenet A - Femminile (HD)' },
    { id: 'it-IT-Wavenet-B', name: 'Wavenet B - Femminile 2 (HD)' },
    { id: 'it-IT-Wavenet-C', name: 'Wavenet C - Maschile (HD)' },
    { id: 'it-IT-Wavenet-D', name: 'Wavenet D - Maschile 2 (HD)' },
  ],
  azure: [
    { id: 'it-IT-ElsaNeural', name: 'Elsa - Femminile' },
    { id: 'it-IT-IsabellaNeural', name: 'Isabella - Femminile 2' },
    { id: 'it-IT-DiegoNeural', name: 'Diego - Maschile' },
    { id: 'it-IT-BenignoNeural', name: 'Benigno - Maschile 2' },
  ],
};

/**
 * Pannello di configurazione per il bot WhatsApp.
 *
 * Include tab per:
 * - Base: modello AI, parametri, opzioni risposta
 * - API Keys: chiavi per OpenAI, ElevenLabs, Google, Azure
 * - Voce: selezione provider e voce TTS con preview
 * - Prompts: configurazione prompt AI
 */
export function ConfigPanel() {
  const { apiBaseUrl } = useWhatsAppConfig();

  const [config, setConfig] = useState<Config | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [activeTab, setActiveTab] = useState<'general' | 'apikeys' | 'tts' | 'prompts'>('general');

  const [elevenLabsCredits, setElevenLabsCredits] = useState<ElevenLabsCredits | null>(null);
  const [loadingCredits, setLoadingCredits] = useState(false);
  const [creditsError, setCreditsError] = useState<string | null>(null);

  const [playingVoice, setPlayingVoice] = useState<string | null>(null);
  const [audioElement, setAudioElement] = useState<HTMLAudioElement | null>(null);

  const [showApiKeys, setShowApiKeys] = useState<{[key: string]: boolean}>({});

  // Prompt editor modal state
  const [promptModal, setPromptModal] = useState<{
    isOpen: boolean;
    promptKey: 'prompt_system' | 'prompt_intent_recognition' | 'prompt_response_generation' | 'prompt_device_query' | null;
    title: string;
    description: string;
  }>({ isOpen: false, promptKey: null, title: '', description: '' });

  // Confirm modal state for reset all prompts
  const [showResetConfirm, setShowResetConfirm] = useState(false);

  // Default prompts (loaded from backend)
  const [defaultPrompts, setDefaultPrompts] = useState<{
    prompt_system: string;
    prompt_intent_recognition: string;
    prompt_response_generation: string;
    prompt_device_query: string;
  } | null>(null);

  useEffect(() => {
    fetchConfig();
    fetchDefaultPrompts();
  }, [apiBaseUrl]);

  const fetchDefaultPrompts = async () => {
    try {
      const response = await fetch(`${apiBaseUrl}/config/default-prompts`, { headers: waAuthHeaders() });
      const data = await response.json();
      if (data.success) {
        setDefaultPrompts(data.prompts);
      }
    } catch (err) {
      console.error('Errore caricamento prompt default:', err);
    }
  };

  const openPromptEditor = (
    promptKey: 'prompt_system' | 'prompt_intent_recognition' | 'prompt_response_generation' | 'prompt_device_query',
    title: string,
    description: string
  ) => {
    setPromptModal({ isOpen: true, promptKey, title, description });
  };

  const closePromptEditor = () => {
    setPromptModal({ isOpen: false, promptKey: null, title: '', description: '' });
  };

  const resetAllPromptsToDefault = () => {
    if (!defaultPrompts || !config) return;
    setShowResetConfirm(true);
  };

  const confirmResetAllPrompts = () => {
    if (!defaultPrompts || !config) return;
    setConfig({
      ...config,
      prompt_system: defaultPrompts.prompt_system,
      prompt_intent_recognition: defaultPrompts.prompt_intent_recognition,
      prompt_response_generation: defaultPrompts.prompt_response_generation,
      prompt_device_query: defaultPrompts.prompt_device_query,
    });
  };

  const fetchConfig = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${apiBaseUrl}/config`, { headers: waAuthHeaders() });
      const data = await response.json();
      if (data.success) {
        setConfig(data.data);
      } else {
        setError(data.error || 'Errore caricamento configurazione');
      }
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const saveConfig = async () => {
    if (!config) return;

    try {
      setSaving(true);
      setError(null);
      setSuccess(false);

      const response = await fetch(`${apiBaseUrl}/config`, {
        method: 'PUT',
        headers: waAuthHeaders({ 'Content-Type': 'application/json' }),
        body: JSON.stringify(config)
      });

      const data = await response.json();
      if (data.success) {
        setSuccess(true);
        setTimeout(() => setSuccess(false), 3000);
      } else {
        setError(data.error || 'Errore salvataggio');
      }
    } catch (err: any) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const updateConfig = (field: keyof Config, value: any) => {
    if (!config) return;
    setConfig({ ...config, [field]: value });
  };

  const fetchElevenLabsCredits = async () => {
    if (!config?.elevenlabs_api_key) {
      setCreditsError('Inserisci prima una API key ElevenLabs');
      return;
    }

    try {
      setLoadingCredits(true);
      setCreditsError(null);

      const response = await fetch('https://api.elevenlabs.io/v1/user/subscription', {
        method: 'GET',
        headers: { 'xi-api-key': config.elevenlabs_api_key }
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail?.message || `Errore ${response.status}`);
      }

      const data = await response.json();
      setElevenLabsCredits(data);
    } catch (err: any) {
      setCreditsError(err.message);
      setElevenLabsCredits(null);
    } finally {
      setLoadingCredits(false);
    }
  };

  const playVoicePreview = async (voiceId: string, provider: string = 'openai') => {
    if (audioElement) {
      audioElement.pause();
      audioElement.src = '';
      setAudioElement(null);
    }

    if (playingVoice === voiceId) {
      setPlayingVoice(null);
      return;
    }

    try {
      setPlayingVoice(voiceId);

      const response = await fetch(`${apiBaseUrl}/config/voice-preview`, {
        method: 'POST',
        headers: waAuthHeaders({ 'Content-Type': 'application/json' }),
        body: JSON.stringify({ voice: voiceId, provider })
      });

      const data = await response.json();

      if (!data.success) {
        throw new Error(data.error);
      }

      const audioBlob = new Blob(
        [Uint8Array.from(atob(data.audio), c => c.charCodeAt(0))],
        { type: data.contentType }
      );
      const audioUrl = URL.createObjectURL(audioBlob);

      const audio = new Audio(audioUrl);
      setAudioElement(audio);

      audio.onended = () => {
        setPlayingVoice(null);
        setAudioElement(null);
        URL.revokeObjectURL(audioUrl);
      };

      audio.onerror = () => {
        setPlayingVoice(null);
        setAudioElement(null);
        URL.revokeObjectURL(audioUrl);
      };

      await audio.play();
    } catch (err: any) {
      console.error('Errore preview voce:', err);
      setPlayingVoice(null);
      setError(err.message || 'Errore generazione anteprima');
    }
  };

  if (loading) {
    return (
      <div className="bg-slate-800 rounded-lg p-6 border border-slate-700">
        <div className="text-center py-8 text-slate-400">
          Caricamento configurazione...
        </div>
      </div>
    );
  }

  if (!config) {
    return (
      <div className="bg-slate-800 rounded-lg p-6 border border-slate-700">
        <div className="p-3 bg-red-900/50 border border-red-700 rounded text-red-200">
          Configurazione non trovata
        </div>
      </div>
    );
  }

  return (
    <div className="bg-slate-800 rounded-lg p-4 border border-slate-700">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-semibold text-white">Configurazione Sistema</h2>
        <button
          onClick={saveConfig}
          disabled={saving}
          className="px-3 py-1.5 bg-green-600 hover:bg-green-700 disabled:bg-slate-600 text-white rounded text-sm font-medium transition"
        >
          {saving ? 'Salvataggio...' : 'Salva'}
        </button>
      </div>

      {success && (
        <div className="mb-3 p-2 bg-green-900/50 border border-green-700 rounded text-green-200 text-xs">
          ✓ Salvato con successo
        </div>
      )}
      {error && (
        <div className="mb-3 p-2 bg-red-900/50 border border-red-700 rounded text-red-200 text-xs">
          {error}
        </div>
      )}

      {/* Tabs */}
      <div className="flex gap-1 mb-4 border-b border-slate-700">
        <button
          onClick={() => setActiveTab('general')}
          className={`px-3 py-1.5 text-sm font-medium transition ${activeTab === 'general' ? 'text-primary-400 border-b-2 border-primary-400' : 'text-slate-400 hover:text-slate-300'}`}
        >
          Base
        </button>
        <button
          onClick={() => setActiveTab('apikeys')}
          className={`px-3 py-1.5 text-sm font-medium transition ${activeTab === 'apikeys' ? 'text-primary-400 border-b-2 border-primary-400' : 'text-slate-400 hover:text-slate-300'}`}
        >
          API Keys
        </button>
        <button
          onClick={() => setActiveTab('tts')}
          className={`px-3 py-1.5 text-sm font-medium transition ${activeTab === 'tts' ? 'text-primary-400 border-b-2 border-primary-400' : 'text-slate-400 hover:text-slate-300'}`}
        >
          Voce
        </button>
        <button
          onClick={() => setActiveTab('prompts')}
          className={`px-3 py-1.5 text-sm font-medium transition ${activeTab === 'prompts' ? 'text-primary-400 border-b-2 border-primary-400' : 'text-slate-400 hover:text-slate-300'}`}
        >
          Prompts
        </button>
      </div>

      {/* Tab Content */}
      <div className="space-y-3">
        {/* GENERAL TAB */}
        {activeTab === 'general' && (
          <div className="space-y-3">
            <div className="grid grid-cols-3 gap-3">
              <div>
                <label className="block text-xs font-medium text-slate-400 mb-1">Modello AI</label>
                <select
                  value={config.openai_model}
                  onChange={(e) => updateConfig('openai_model', e.target.value)}
                  className="w-full px-2 py-1.5 bg-slate-700 border border-slate-600 rounded text-white text-sm focus:outline-none focus:border-primary-500"
                >
                  <option value="gpt-4-turbo-preview">GPT-4 Turbo</option>
                  <option value="gpt-4">GPT-4</option>
                  <option value="gpt-3.5-turbo">GPT-3.5 Turbo</option>
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-400 mb-1">Max Tokens</label>
                <input
                  type="number"
                  value={config.max_tokens}
                  onChange={(e) => updateConfig('max_tokens', parseInt(e.target.value))}
                  className="w-full px-2 py-1.5 bg-slate-700 border border-slate-600 rounded text-white text-sm focus:outline-none focus:border-primary-500"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-400 mb-1">Temperature</label>
                <input
                  type="number"
                  step="0.1"
                  min="0"
                  max="1"
                  value={config.temperature}
                  onChange={(e) => updateConfig('temperature', parseFloat(e.target.value))}
                  className="w-full px-2 py-1.5 bg-slate-700 border border-slate-600 rounded text-white text-sm focus:outline-none focus:border-primary-500"
                />
              </div>
            </div>

            {/* Bot Master Switch */}
            <div className={`p-3 rounded border ${config.is_active ? 'bg-green-900/30 border-green-600' : 'bg-red-900/30 border-red-600'}`}>
              <label className="flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={config.is_active}
                  onChange={(e) => updateConfig('is_active', e.target.checked)}
                  className="mr-3 w-5 h-5"
                />
                <div>
                  <span className={`font-semibold ${config.is_active ? 'text-green-400' : 'text-red-400'}`}>
                    {config.is_active ? '🟢 Bot ATTIVO' : '🔴 Bot DISATTIVATO'}
                  </span>
                  <p className="text-xs text-slate-400 mt-0.5">
                    {config.is_active
                      ? 'Il bot risponde ai messaggi in arrivo'
                      : 'Il bot ignora tutti i messaggi in arrivo'}
                  </p>
                </div>
              </label>
            </div>

            <div className="flex items-center gap-6 p-3 bg-slate-700/50 rounded">
              <label className="flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={config.auto_response}
                  onChange={(e) => updateConfig('auto_response', e.target.checked)}
                  className="mr-2 w-4 h-4"
                />
                <span className="text-slate-300 text-sm">Risposta Automatica</span>
              </label>
              <label className="flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={config.audio_response_enabled}
                  onChange={(e) => updateConfig('audio_response_enabled', e.target.checked)}
                  className="mr-2 w-4 h-4"
                />
                <span className="text-slate-300 text-sm">Risposte Audio</span>
              </label>
              <label className="flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={config.reply_only_known_numbers}
                  onChange={(e) => updateConfig('reply_only_known_numbers', e.target.checked)}
                  className="mr-2 w-4 h-4"
                />
                <span className="text-slate-300 text-sm">Solo Numeri Autorizzati</span>
              </label>
            </div>
          </div>
        )}

        {/* API KEYS TAB */}
        {activeTab === 'apikeys' && (
          <div className="space-y-3">
            <div>
              <label className="block text-xs font-medium text-slate-400 mb-1">OpenAI API Key</label>
              <div className="relative">
                <input
                  type={showApiKeys['openai'] ? 'text' : 'password'}
                  value={config.openai_api_key || ''}
                  onChange={(e) => updateConfig('openai_api_key', e.target.value)}
                  placeholder="sk-..."
                  className="w-full px-2 py-1.5 pr-10 bg-slate-700 border border-slate-600 rounded text-white placeholder-slate-500 text-sm focus:outline-none focus:border-primary-500 font-mono"
                />
                <button
                  type="button"
                  onClick={() => setShowApiKeys(prev => ({ ...prev, openai: !prev.openai }))}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-white transition"
                  title={showApiKeys['openai'] ? 'Nascondi' : 'Mostra'}
                >
                  {showApiKeys['openai'] ? '👁️' : '👁️‍🗨️'}
                </button>
              </div>
            </div>

            <div>
              <label className="block text-xs font-medium text-slate-400 mb-1">ElevenLabs API Key</label>
              <div className="relative">
                <input
                  type={showApiKeys['elevenlabs'] ? 'text' : 'password'}
                  value={config.elevenlabs_api_key || ''}
                  onChange={(e) => updateConfig('elevenlabs_api_key', e.target.value)}
                  placeholder="Per TTS alta qualità"
                  className="w-full px-2 py-1.5 pr-10 bg-slate-700 border border-slate-600 rounded text-white placeholder-slate-500 text-sm focus:outline-none focus:border-primary-500 font-mono"
                />
                <button
                  type="button"
                  onClick={() => setShowApiKeys(prev => ({ ...prev, elevenlabs: !prev.elevenlabs }))}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-white transition"
                  title={showApiKeys['elevenlabs'] ? 'Nascondi' : 'Mostra'}
                >
                  {showApiKeys['elevenlabs'] ? '👁️' : '👁️‍🗨️'}
                </button>
              </div>

              {config.elevenlabs_api_key && (
                <div className="mt-2 flex items-center gap-2">
                  <button
                    onClick={fetchElevenLabsCredits}
                    disabled={loadingCredits}
                    className="px-2 py-1 bg-primary-600 hover:bg-primary-700 disabled:bg-slate-600 text-white rounded text-xs font-medium transition"
                  >
                    {loadingCredits ? 'Caricamento...' : 'Verifica Crediti'}
                  </button>

                  {elevenLabsCredits && (
                    <div className="flex-1 text-xs text-slate-300">
                      <span className="text-green-400 font-medium">
                        {elevenLabsCredits.character_limit && elevenLabsCredits.character_count
                          ? `${(elevenLabsCredits.character_limit - elevenLabsCredits.character_count).toLocaleString()} caratteri residui`
                          : elevenLabsCredits.tier || 'Attivo'}
                      </span>
                    </div>
                  )}

                  {creditsError && (
                    <span className="text-xs text-red-400">{creditsError}</span>
                  )}
                </div>
              )}
            </div>

            <div>
              <label className="block text-xs font-medium text-slate-400 mb-1">Google Cloud TTS Key</label>
              <div className="relative">
                <input
                  type={showApiKeys['google'] ? 'text' : 'password'}
                  value={config.google_tts_api_key || ''}
                  onChange={(e) => updateConfig('google_tts_api_key', e.target.value)}
                  placeholder="Opzionale"
                  className="w-full px-2 py-1.5 pr-10 bg-slate-700 border border-slate-600 rounded text-white placeholder-slate-500 text-sm focus:outline-none focus:border-primary-500 font-mono"
                />
                <button
                  type="button"
                  onClick={() => setShowApiKeys(prev => ({ ...prev, google: !prev.google }))}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-white transition"
                  title={showApiKeys['google'] ? 'Nascondi' : 'Mostra'}
                >
                  {showApiKeys['google'] ? '👁️' : '👁️‍🗨️'}
                </button>
              </div>
            </div>

            <div>
              <label className="block text-xs font-medium text-slate-400 mb-1">Azure TTS Key</label>
              <div className="relative">
                <input
                  type={showApiKeys['azure'] ? 'text' : 'password'}
                  value={config.azure_tts_api_key || ''}
                  onChange={(e) => updateConfig('azure_tts_api_key', e.target.value)}
                  placeholder="Opzionale"
                  className="w-full px-2 py-1.5 pr-10 bg-slate-700 border border-slate-600 rounded text-white placeholder-slate-500 text-sm focus:outline-none focus:border-primary-500 font-mono"
                />
                <button
                  type="button"
                  onClick={() => setShowApiKeys(prev => ({ ...prev, azure: !prev.azure }))}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-white transition"
                  title={showApiKeys['azure'] ? 'Nascondi' : 'Mostra'}
                >
                  {showApiKeys['azure'] ? '👁️' : '👁️‍🗨️'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* TTS TAB */}
        {activeTab === 'tts' && (
          <div className="space-y-3">
            <div>
              <label className="block text-xs font-medium text-slate-400 mb-1">Provider TTS</label>
              <select
                value={config.tts_provider || 'openai'}
                onChange={(e) => updateConfig('tts_provider', e.target.value)}
                className="w-full px-2 py-1.5 bg-slate-700 border border-slate-600 rounded text-white text-sm focus:outline-none focus:border-primary-500"
              >
                <option value="openai">OpenAI TTS</option>
                <option value="elevenlabs">ElevenLabs</option>
                <option value="google">Google Cloud</option>
                <option value="azure">Azure TTS</option>
              </select>
            </div>

            {/* OpenAI Voices */}
            {(config.tts_provider === 'openai' || !config.tts_provider) && (
              <div>
                <label className="block text-xs font-medium text-slate-400 mb-2">Seleziona Voce (clicca ▶ per anteprima)</label>
                <div className="space-y-1 max-h-48 overflow-y-auto">
                  {VOICE_PRESETS.openai.map((voice) => (
                    <div
                      key={voice.id}
                      className={`flex items-center justify-between p-2 rounded border cursor-pointer transition ${
                        config.tts_voice === voice.id
                          ? 'bg-primary-600/30 border-primary-500'
                          : 'bg-slate-700/50 border-slate-600 hover:border-slate-500'
                      }`}
                      onClick={() => updateConfig('tts_voice', voice.id)}
                    >
                      <div className="flex items-center gap-2">
                        <input
                          type="radio"
                          name="openai_voice"
                          checked={config.tts_voice === voice.id}
                          onChange={() => updateConfig('tts_voice', voice.id)}
                          className="w-3 h-3"
                        />
                        <span className="text-sm text-white">{voice.name}</span>
                      </div>
                      <button
                        onClick={(e) => { e.stopPropagation(); playVoicePreview(voice.id, 'openai'); }}
                        disabled={playingVoice !== null && playingVoice !== voice.id}
                        className={`px-2 py-1 rounded text-xs font-medium transition ${
                          playingVoice === voice.id
                            ? 'bg-red-600 text-white animate-pulse'
                            : 'bg-slate-600 hover:bg-slate-500 text-white'
                        } disabled:opacity-50`}
                      >
                        {playingVoice === voice.id ? '⏹' : '▶'}
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* ElevenLabs Voices */}
            {config.tts_provider === 'elevenlabs' && (
              <div>
                <label className="block text-xs font-medium text-slate-400 mb-2">Seleziona Voce (clicca ▶ per anteprima)</label>
                <div className="space-y-1 max-h-48 overflow-y-auto">
                  {VOICE_PRESETS.elevenlabs.map((voice) => (
                    <div
                      key={voice.id}
                      className={`flex items-center justify-between p-2 rounded border cursor-pointer transition ${
                        config.tts_voice_elevenlabs === voice.id
                          ? 'bg-primary-600/30 border-primary-500'
                          : 'bg-slate-700/50 border-slate-600 hover:border-slate-500'
                      }`}
                      onClick={() => updateConfig('tts_voice_elevenlabs', voice.id)}
                    >
                      <div className="flex items-center gap-2">
                        <input
                          type="radio"
                          name="elevenlabs_voice"
                          checked={config.tts_voice_elevenlabs === voice.id}
                          onChange={() => updateConfig('tts_voice_elevenlabs', voice.id)}
                          className="w-3 h-3"
                        />
                        <span className="text-sm text-white">{voice.name} <span className="text-slate-400 text-xs">({voice.id})</span></span>
                      </div>
                      <button
                        onClick={(e) => { e.stopPropagation(); playVoicePreview(voice.id, 'elevenlabs'); }}
                        disabled={playingVoice !== null && playingVoice !== voice.id}
                        className={`px-2 py-1 rounded text-xs font-medium transition ${
                          playingVoice === voice.id
                            ? 'bg-red-600 text-white animate-pulse'
                            : 'bg-slate-600 hover:bg-slate-500 text-white'
                        } disabled:opacity-50`}
                      >
                        {playingVoice === voice.id ? '⏹' : '▶'}
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Google Voices */}
            {config.tts_provider === 'google' && (
              <div>
                <label className="block text-xs font-medium text-slate-400 mb-1">Voce Google</label>
                <select
                  value={config.tts_voice_google || ''}
                  onChange={(e) => updateConfig('tts_voice_google', e.target.value)}
                  className="w-full px-2 py-1.5 bg-slate-700 border border-slate-600 rounded text-white text-sm focus:outline-none focus:border-primary-500"
                >
                  <option value="">Seleziona voce...</option>
                  {VOICE_PRESETS.google.map((voice) => (
                    <option key={voice.id} value={voice.id}>{voice.name}</option>
                  ))}
                </select>
              </div>
            )}

            {/* Azure Voices */}
            {config.tts_provider === 'azure' && (
              <div>
                <label className="block text-xs font-medium text-slate-400 mb-1">Voce Azure</label>
                <select
                  value={config.tts_voice_azure || ''}
                  onChange={(e) => updateConfig('tts_voice_azure', e.target.value)}
                  className="w-full px-2 py-1.5 bg-slate-700 border border-slate-600 rounded text-white text-sm focus:outline-none focus:border-primary-500"
                >
                  <option value="">Seleziona voce...</option>
                  {VOICE_PRESETS.azure.map((voice) => (
                    <option key={voice.id} value={voice.id}>{voice.name}</option>
                  ))}
                </select>
              </div>
            )}
          </div>
        )}

        {/* PROMPTS TAB */}
        {activeTab === 'prompts' && (
          <div className="space-y-3">
            <div className="flex items-center justify-between mb-2">
              <p className="text-xs text-slate-400">
                Clicca su un prompt per modificarlo in una finestra dedicata
              </p>
              <button
                onClick={resetAllPromptsToDefault}
                disabled={!defaultPrompts}
                className="px-3 py-1.5 bg-yellow-600 hover:bg-yellow-700 disabled:bg-slate-600 text-white rounded text-xs font-medium transition flex items-center gap-1"
              >
                🔄 Reset Tutti i Prompt
              </button>
            </div>

            {/* Prompt Sistema */}
            <div
              onClick={() => openPromptEditor('prompt_system', 'Prompt Sistema', 'Definisce il ruolo e comportamento base dell\'AI')}
              className="p-3 bg-slate-700/50 border border-slate-600 rounded hover:border-primary-500 cursor-pointer transition group"
            >
              <div className="flex items-center justify-between mb-1">
                <span className="text-sm font-medium text-white">🤖 Prompt Sistema</span>
                <span className="text-xs text-primary-400 opacity-0 group-hover:opacity-100 transition">Clicca per modificare →</span>
              </div>
              <p className="text-xs text-slate-400 truncate">
                {config.prompt_system ? `${config.prompt_system.substring(0, 80)}...` : '(Non configurato - usa default)'}
              </p>
            </div>

            {/* Prompt Intent Recognition */}
            <div
              onClick={() => openPromptEditor('prompt_intent_recognition', 'Riconoscimento Intent', 'Come l\'AI identifica l\'intento dell\'utente dal messaggio')}
              className="p-3 bg-slate-700/50 border border-slate-600 rounded hover:border-primary-500 cursor-pointer transition group"
            >
              <div className="flex items-center justify-between mb-1">
                <span className="text-sm font-medium text-white">🎯 Riconoscimento Intent</span>
                <span className="text-xs text-primary-400 opacity-0 group-hover:opacity-100 transition">Clicca per modificare →</span>
              </div>
              <p className="text-xs text-slate-400 truncate">
                {config.prompt_intent_recognition ? `${config.prompt_intent_recognition.substring(0, 80)}...` : '(Non configurato - usa default)'}
              </p>
            </div>

            {/* Prompt Response Generation */}
            <div
              onClick={() => openPromptEditor('prompt_response_generation', 'Generazione Risposta', 'Come l\'AI genera le risposte per l\'utente')}
              className="p-3 bg-slate-700/50 border border-slate-600 rounded hover:border-primary-500 cursor-pointer transition group"
            >
              <div className="flex items-center justify-between mb-1">
                <span className="text-sm font-medium text-white">💬 Generazione Risposta</span>
                <span className="text-xs text-primary-400 opacity-0 group-hover:opacity-100 transition">Clicca per modificare →</span>
              </div>
              <p className="text-xs text-slate-400 truncate">
                {config.prompt_response_generation ? `${config.prompt_response_generation.substring(0, 80)}...` : '(Non configurato - usa default)'}
              </p>
            </div>

            {/* Prompt Device Query */}
            <div
              onClick={() => openPromptEditor('prompt_device_query', 'Query Database', 'Come l\'AI interpreta i dati del database per rispondere')}
              className="p-3 bg-slate-700/50 border border-slate-600 rounded hover:border-primary-500 cursor-pointer transition group"
            >
              <div className="flex items-center justify-between mb-1">
                <span className="text-sm font-medium text-white">🗄️ Query Database</span>
                <span className="text-xs text-primary-400 opacity-0 group-hover:opacity-100 transition">Clicca per modificare →</span>
              </div>
              <p className="text-xs text-slate-400 truncate">
                {config.prompt_device_query ? `${config.prompt_device_query.substring(0, 80)}...` : '(Non configurato - usa default)'}
              </p>
            </div>

            <div className="p-2 bg-blue-900/30 border border-blue-700/50 rounded text-xs text-blue-300">
              💡 <strong>Nota:</strong> Se un prompt è vuoto, verrà usato il prompt di default del sistema.
            </div>
          </div>
        )}
      </div>

      {/* Prompt Editor Modal */}
      {promptModal.promptKey && (
        <PromptEditorModal
          isOpen={promptModal.isOpen}
          onClose={closePromptEditor}
          title={promptModal.title}
          description={promptModal.description}
          value={config[promptModal.promptKey] || ''}
          onChange={(value) => updateConfig(promptModal.promptKey!, value)}
          defaultValue={defaultPrompts?.[promptModal.promptKey] || ''}
        />
      )}

      {/* Reset All Prompts Confirmation Modal */}
      <ConfirmModal
        isOpen={showResetConfirm}
        onClose={() => setShowResetConfirm(false)}
        onConfirm={confirmResetAllPrompts}
        title="Ripristina Prompt"
        message="Sei sicuro di voler ripristinare TUTTI i prompt ai valori di default? I prompt attuali verranno sostituiti con quelli predefiniti del sistema."
        confirmText="Ripristina Tutti"
        cancelText="Annulla"
        confirmColor="yellow"
      />
    </div>
  );
}
